/// <amd-module name="Pipeline/Analytics/PipelineOutcome/PassRateTrendCard/ControllerViews/PipelineOutcomePassRateTrendCard" />
import { ErrorComponent } from "Analytics/Shared/Analytics-Visuals-Common/ErrorComponent/ErrorComponent";
import { PassPercentSeries } from "Analytics/Shared/ChartSeries/PassPercentSeries";
import { ITrendData, TrendType } from "Analytics/Shared/Common/Types";
import { getTrendFromData } from "Analytics/Shared/Common/Utils/TrendCalculator";
import { ITrendChartData } from "Analytics/Shared/TrendChart/Common/Types";
import * as Utils_Number from "Analytics/Shared/Utils/Number";
import { AreaChartHelper } from "Analytics/Shared/Visuals/AreaChartView/Common/AreaChartHelper";
import { AreaChartView } from "Analytics/Shared/Visuals/AreaChartView/ControllerViews/AreaChartView";
import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import { DonutChart, IDonutChartProps } from "Analytics/Shared/Visuals/DonutChart/Components/DonutChart";
import { IPipelineOutcomeData } from "Pipeline/Analytics/Common/Types";
import * as Resources from "Pipeline/Analytics/PipelineOutcome/PassRateTrendCard/Resources";
import {
    IPipelineOutcomePassRateTrendState,
    PipelineOutcomePassRateTrendStore
} from "Pipeline/Analytics/PipelineOutcome/PassRateTrendCard/Stores/PipelineOutcomePassRateTrendStore";
import * as React from "react";
import { IDonutChartVisualDatum } from "ReportsCommon/DonutChartVisual/Components/DonutChartVisual";
import { TestReportColorPalette } from "Test/common-util/Common";
import { localeFormat } from "VSS/Core/Util/String";
import { ChartHostOptions, ChartProps, LabelFormatModes } from "VSS/Features/Charts/Contracts";
import { DataPoint, TooltipLineItem } from "VSS/Features/Charts/Contracts";
import * as Charts_Contracts from "VSS/Features/Charts/Contracts";
import { css, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Components/Card/Card";
import { Icon } from "VSSUI/Icon";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

export interface IPipelineOutcomePassRateTrendCardProps extends IVssComponentProperties {
    store: PipelineOutcomePassRateTrendStore;
    headerClassName?: string;
    onDataRendered?: () => void;
    errorText?: string;
    updatePipelineData?: (data: IPipelineOutcomeData | undefined) => void;
}

enum WidgetCharts {
    DonutChart_PassRateTrend="DonutChart_PassRateTrend",
    AreaChart_PassRateTrend="AreaChart_PassRateTrend"
}

export class PipelineOutcomePassRateTrendCard extends VssComponent<IPipelineOutcomePassRateTrendCardProps, IPipelineOutcomePassRateTrendState> {
    private _onDataRenderedCalled = false;
    private _onUpdatePipelineDataCalled = false;
    private isChartClicked = {
        [WidgetCharts.DonutChart_PassRateTrend]: false,
        [WidgetCharts.AreaChart_PassRateTrend]: false
    }

    constructor(props: IPipelineOutcomePassRateTrendCardProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            errorText: this.props.errorText
        } as Readonly<IPipelineOutcomePassRateTrendState>;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (this.props) {
            this.props.store.addChangedListener(this._onStoreUpdate);
            this.setState(this.props.store.getState());
            this.performOnDataRendered();
        }
    }

    public componentDidUpdate(prevProps: IPipelineOutcomePassRateTrendCardProps, prevState: IPipelineOutcomePassRateTrendState): void {
        super.componentDidUpdate(prevProps, prevState);
        this.performOnDataRendered();
    }

    public componentWillUnmount() {
        if (this.props) {
            this.props.store.removeChangedListener(this._onStoreUpdate);
        }
        super.componentWillUnmount();
    }

    public render(): JSX.Element {
        return (
            <Card className={this.props.className} titleProps={{ text: Resources.CardTitle, ariaLevel: 2 }} headerClassName={this.props.headerClassName}>
                {this._getElementToDisplay()}
            </Card>
        );
    }

    protected _getPieChartProps(): IDonutChartProps {
        const props: IDonutChartProps = {
            title: Resources.PassRatePieChartAriaLabelText,
            chartData: this._getDonutChartData(),
            pageContext: this.context.pageContext,
            chartWidth: 80,
            chartHeight: 100,
            chartDiameter: 80,
            chartInnerSize: 50,
            enableLegends: false,
            className: "donut-chart",
            customizeTooltip: value => `${value.toLocaleString()}`,
            onChartClick: (event: Charts_Contracts.ClickEvent) => {
                if(!this.isChartClicked[WidgetCharts.DonutChart_PassRateTrend]){
                    this.publishWidgetChartClickTelemetry(WidgetCharts.DonutChart_PassRateTrend);
                }
                this.isChartClicked[WidgetCharts.DonutChart_PassRateTrend] = true;
            }
        };

        return props;
    }

    protected _getDonutChartData(): IDonutChartVisualDatum[] {
        if (this.state.data) {
            const passRateData = this.state.data!.pipelineOutcomePassRateData;
            const passedCount = passRateData ? passRateData.passedCount : 0;
            const failedCount = passRateData ? passRateData.failedCount : 0;
            return [
                {
                    name: Resources.PassedRunsText,
                    legendLabel: Resources.Outcome_Passed,
                    color: TestReportColorPalette.Passed,
                    y: passedCount,
                    legendDisplayValue: Utils_Number.formatAbbreviatedNumber(passedCount)
                } as IDonutChartVisualDatum,
                {
                    name: Resources.FailedRunsText,
                    legendLabel: Resources.Outcome_Failed,
                    color: TestReportColorPalette.Failed,
                    y: failedCount,
                    legendDisplayValue: Utils_Number.formatAbbreviatedNumber(failedCount)
                } as IDonutChartVisualDatum
            ];
        } else {
            return [];
        }
    }

    private performOnDataRendered() {
        if (this.state.data || this.state.errorText) {
            if (this.props.onDataRendered && !this._onDataRenderedCalled) {
                this.props.onDataRendered();
                this._onDataRenderedCalled = true;
            }
            if (this.props.updatePipelineData && !this._onUpdatePipelineDataCalled) {
                this.props.updatePipelineData(this.state.data);
                this._onUpdatePipelineDataCalled = true;
            }
        }
    }

    private _getElementToDisplay(): JSX.Element {
        if (this.state.errorText) {
            return <ErrorComponent className="pipeline-outcome-passratetrend-container flex-grow" errorMessage={this.state.errorText} />;
        }
        if (this.state.confValues != null && this.state.data != null) {
            const passRateData = this.state.data.pipelineOutcomePassRateData;
            const trendData = this.state.data.trendChartData;
            let passedCount = 0;
            let trend: ITrendData | undefined;
            if (passRateData.totalCount > 0) {
                trendData.secondaryChartData = trendData.secondaryChartData!.filter(val => {
                    if (val.aggrValue != null) {
                        return val;
                    }
                });
                passedCount = passRateData.passedCount;
                const len = trendData.secondaryChartData!.length;
                const startValue = trendData.secondaryChartData![0].aggrValue;
                const endValue = trendData.secondaryChartData![len - 1].aggrValue;
                trend = getTrendFromData(startValue, endValue, false);
            }
            return (
                <div className="pipeline-outcome-passratetrend-container flex-grow">
                    {passRateData.totalCount > 0 && (
                        <div className="flex-row flex-wrap">
                            <div className="pipeline-passrate-summary flex-column">
                                <div className="flex-row flex-grow flex-center">
                                    <DonutChart className={css("pass-rate-chart-view-section", this.props.className)} {...this._getPieChartProps()} />
                                    {this._getMetricElement(passRateData.passPercentage, passRateData.totalCount, passedCount, trend)}
                                </div>
                            </div>
                            <div className="pipeline-passrate-trend">
                                {this._verifyChartData(trendData) && <AreaChartView chartOptions={this._getChartOptions(trendData)} />}
                            </div>
                        </div>
                    )}
                    {passRateData.totalCount == 0 && <AnalyticsUnavailableMessage suggestion={Resources.NoPipelineRunText} iconName="Rocket" />}
                </div>
            );
        } else {
            return <Spinner className={css("analytics-card-spinner flex-grow flex-column")} size={SpinnerSize.large} />;
        }
    }

    private _getMetricElement(passRate: number, totalCount: number, passCount: number, trend?: ITrendData): JSX.Element {
        return (
            <div className="card-content flex-column">
                <div className="flex-row">
                    <div className="text-left font-size-lll">
                        {passRate}
                        <span className="font-size-ll">{Resources.Percentage}</span>
                    </div>
                    {trend && (
                        <div
                            className={css(
                                "pipeline-trend fontWeightSemiBold flex-self-end",
                                trend.trendType === TrendType.Increased ? "positive-color" : "negative-color"
                            )}
                        >
                            <Icon 
                                className="trend-icon font-size-xs" 
                                iconName={trend.trendType === TrendType.Increased ? "TriangleSolidUp12" : "TriangleSolidDown12"} 
                            />
                            <span>
                                {localeFormat(Resources.TrendFormat, trend.percentageVariation || 0)}
                            </span>
                        </div>
                    )}
                </div>
                <div className="card-label text-left font-size-m text-ellipsis">
                    {localeFormat(Resources.PassedPipelineFormat, passCount.toLocaleString(), totalCount.toLocaleString())}
                </div>
            </div>
        );
    }

    private _getChartOptions = (chartData: ITrendChartData): ChartProps | undefined => {
        const _areaChartHelper: AreaChartHelper = new AreaChartHelper();
        const chartSeries = new PassPercentSeries(chartData.secondaryChartData!.map(d => d.aggrValue));
        let chartProps = _areaChartHelper.getAreaChartOptions(this.context, chartData.secondaryChartData!, chartSeries, false, this.state.confValues);
        chartProps = _areaChartHelper.setAreaChartTitle(chartProps, Resources.PassRateTrendChartAriaLabelText);
        chartProps = _areaChartHelper.setHostOptions(chartProps, { height: 200 } as ChartHostOptions);
        chartProps = _areaChartHelper.setSecondaryAxisLabelFormatMode(chartProps, LabelFormatModes.Percentage, chartSeries);
        chartProps = _areaChartHelper.setCustomTooltipMapping(chartProps, this._customTooltipMapping);
        chartProps.click = () => {
            if(!this.isChartClicked[WidgetCharts.AreaChart_PassRateTrend]){
                this.publishWidgetChartClickTelemetry(WidgetCharts.AreaChart_PassRateTrend);
            }
            this.isChartClicked[WidgetCharts.AreaChart_PassRateTrend] = true;
        }
        return chartProps;
    };

    private _customTooltipMapping(points: DataPoint[]): TooltipLineItem[] {
        return points.map(point => {
            const item = {
                styleType: {
                    color: point.color,
                    type: Charts_Contracts.TooltipLineItemMarkerType.Square
                },
                text: ""
            } as Charts_Contracts.TooltipLineItem;
            const seriesValue = Number(point.values[0]).toLocaleString(navigator.language);

            item.text = `${seriesValue}%`;
            return item;
        });
    }

    private _verifyChartData = (chartData: ITrendChartData | undefined): boolean => {
        return chartData && chartData.secondaryChartData && chartData.secondaryChartData.length ? true : false;
    };

    private _onStoreUpdate = () => {
        this.setState(this.props.store.getState());
    };

    private publishWidgetChartClickTelemetry = (chartType: WidgetCharts) => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("PipelineAnalytics", "WidgetChartClick", {
            Chart: chartType
        })
    }
}
